import React from 'react';
import PageLayout from '../components/common/PageLayout';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Tooltip
} from '@mui/material';
import { get, post, put, del } from '../utils/api';
import settings from '../config/settings';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import { useAuth } from '../contexts/AuthContext';

const DEFAULT_TIME_ZONE = 'America/Chicago';
const DURATION_OPTIONS = [30, 60, 90, 120, 150, 180];

const formatDateInZoneWithZone = (iso, zone) => {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: zone || DEFAULT_TIME_ZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(new Date(iso));
  } catch {
    return '';
  }
};

const formatTimeInZoneWithZone = (iso, zone) => {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: zone || DEFAULT_TIME_ZONE,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(new Date(iso));
  } catch {
    return '';
  }
};

const formatDateTimeInZoneWithZone = (iso, zone) => {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: zone || DEFAULT_TIME_ZONE,
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(new Date(iso));
  } catch {
    return '';
  }
};

const getTimeZoneOffsetMinutes = (date, zone) => {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: zone || DEFAULT_TIME_ZONE,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
  const parts = dtf.formatToParts(date);
  const values = {};
  parts.forEach((p) => { values[p.type] = p.value; });
  const asUtc = Date.UTC(
    Number(values.year),
    Number(values.month) - 1,
    Number(values.day),
    Number(values.hour),
    Number(values.minute),
    Number(values.second)
  );
  return (asUtc - date.getTime()) / 60000;
};

const buildDateInZone = (dateStr, timeStr, zone) => {
  if (!dateStr || !timeStr) return null;
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hour, minute] = timeStr.split(':').map(Number);
  if (!year || !month || !day) return null;
  const utcGuess = Date.UTC(year, month - 1, day, hour || 0, minute || 0, 0);
  const offsetMinutes = getTimeZoneOffsetMinutes(new Date(utcGuess), zone);
  return new Date(utcGuess - (offsetMinutes * 60000));
};

const formatDateTimeAmPm = (dateValue, zone) => {
  if (!dateValue) return '';
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: zone || DEFAULT_TIME_ZONE,
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(dateValue);
  } catch {
    return '';
  }
};

const normalizeMeetingPayload = (form) => {
  const startTime = `${form.date}T${form.time}:00`;
  return {
    title: form.title.trim(),
    agenda: String(form.agenda || '').trim(),
    start_time: startTime,
    duration_minutes: Number(form.duration)
  };
};

const buildStartDateTime = (form, zone) => {
  const dt = buildDateInZone(form?.date, form?.time, zone);
  return dt && !isNaN(dt.getTime()) ? dt : null;
};

export default function MeetingsPage() {
  const { staff } = useAuth();
  const [filter, setFilter] = React.useState('upcoming');
  const [timeZone, setTimeZone] = React.useState(DEFAULT_TIME_ZONE);
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [ok, setOk] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [pageSize, setPageSize] = React.useState(50);
  const [nextPageToken, setNextPageToken] = React.useState(null);
  const [pageStack, setPageStack] = React.useState([]);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [viewOpen, setViewOpen] = React.useState(false);
  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [createdOpen, setCreatedOpen] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [viewing, setViewing] = React.useState(null);
  const [canceling, setCanceling] = React.useState(null);
  const [createdMeeting, setCreatedMeeting] = React.useState(null);
  const [editSuccess, setEditSuccess] = React.useState('');
  const [inviteEmails, setInviteEmails] = React.useState('');
  const [inviteSending, setInviteSending] = React.useState(false);
  const [inviteStatus, setInviteStatus] = React.useState('');
  const [inviteDialogOpen, setInviteDialogOpen] = React.useState(false);
  const [inviteMeeting, setInviteMeeting] = React.useState(null);
  const [dialogError, setDialogError] = React.useState('');
  const [dialogSaving, setDialogSaving] = React.useState(false);
  const [dialogFieldErrors, setDialogFieldErrors] = React.useState({
    title: '',
    date: '',
    time: '',
    duration: ''
  });

  const role = String(staff?.role || '').toLowerCase();
  const permissions = Array.isArray(staff?.permissions) ? staff.permissions : [];
  const hasPermission = (key) => {
    if (permissions.includes('admin:full')) return true;
    if (permissions.includes(key)) return true;
    // fallback to legacy role if permissions are missing
    if (permissions.length === 0) {
      if (role === 'admin') return true;
      if (key.startsWith('meetings:')) return role === 'coordinator';
    }
    return false;
  };
  const canAccessMeetings = hasPermission('meetings:access');
  if (!canAccessMeetings) {
    return <Typography sx={{ p: 2 }}>Not authorized to access meetings.</Typography>;
  }

  const [form, setForm] = React.useState({
    title: '',
    date: '',
    time: '',
    duration: 60,
    agenda: ''
  });

  React.useEffect(() => {
    let active = true;
    settings.loadBackendConfig()
      .then((cfg) => {
        const tz = cfg?.zoom?.timezone;
        if (active && typeof tz === 'string' && tz.trim()) {
          setTimeZone(tz.trim());
        }
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const load = React.useCallback(async (tokenOverride = undefined) => {
    setLoading(true);
    setError('');
    try {
      const params = {
        filter,
        limit: pageSize
      };
      if (tokenOverride) {
        params.next_page_token = tokenOverride;
      }
      const data = await get('/api/zoom/meetings', params, { timeout: 60000, cache: 'no-store' });
      setRows(Array.isArray(data?.data) ? data.data : []);
      setNextPageToken(data?.next_page_token || null);
    } catch (e) {
      setError(e?.message || 'Failed to load meetings');
    } finally {
      setLoading(false);
    }
  }, [filter, pageSize]);

  React.useEffect(() => {
    setPageStack([]);
    setNextPageToken(null);
    load().catch(() => {});
  }, [load, filter, pageSize]);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: '', date: '', time: '', duration: 60, agenda: '' });
    setDialogOpen(true);
    setOk('');
    setError('');
    setEditSuccess('');
    setInviteEmails('');
    setInviteStatus('');
    setDialogError('');
    setDialogSaving(false);
    setDialogFieldErrors({ title: '', date: '', time: '', duration: '' });
  };

  const openEdit = (meeting) => {
    setEditing(meeting);
    const date = formatDateInZoneWithZone(meeting.start_time, timeZone);
    const time = formatTimeInZoneWithZone(meeting.start_time, timeZone);
    setForm({
      title: meeting.title || '',
      date,
      time,
      duration: meeting.duration_minutes || 60,
      agenda: meeting.agenda || ''
    });
    setDialogOpen(true);
    setOk('');
    setError('');
    setEditSuccess('');
    setInviteStatus('');
    setDialogError('');
    setDialogSaving(false);
    setDialogFieldErrors({ title: '', date: '', time: '', duration: '' });
  };

  const openView = (meeting) => {
    setViewing(meeting);
    setViewOpen(true);
  };

  const openCancel = (meeting) => {
    setCanceling(meeting);
    setCancelOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setEditSuccess('');
    setDialogError('');
    setDialogSaving(false);
    setDialogFieldErrors({ title: '', date: '', time: '', duration: '' });
  };

  const validateMeetingForm = () => {
    const next = { title: '', date: '', time: '', duration: '' };
    if (!String(form.title || '').trim()) next.title = 'Title is required';
    if (!String(form.date || '').trim()) next.date = 'Start date is required';
    if (!String(form.time || '').trim()) next.time = 'Start time is required';
    if (!form.duration) next.duration = 'Duration is required';
    setDialogFieldErrors(next);
    return !next.title && !next.date && !next.time && !next.duration;
  };

  const openInviteDialog = (meeting) => {
    setInviteMeeting(meeting || null);
    setInviteEmails('');
    setInviteStatus('');
    setInviteDialogOpen(true);
  };

  const saveMeeting = async () => {
    setDialogError('');
    setOk('');
    setEditSuccess('');
    setInviteStatus('');
    setDialogSaving(true);
    try {
      if (!validateMeetingForm()) {
        setDialogError('Please fill all required fields.');
        return;
      }
      const startDt = buildStartDateTime(form, timeZone);
      if (!startDt) {
        setDialogError('Start date and time are required');
        return;
      }
      if (startDt.getTime() < Date.now()) {
        setDialogError('Start time must be in the future');
        return;
      }
      const payload = normalizeMeetingPayload(form);
      if (editing?.id) {
        await put(`/api/zoom/meetings/${editing.id}`, payload);
        setEditSuccess('Meeting updated successfully.');
        if (staff?.email) {
          await post(`/api/zoom/meetings/${editing.id}/invitation-email`, { to: [staff.email] });
          setInviteStatus('Invitation email sent to you.');
        }
      } else {
        const created = await post('/api/zoom/meetings', payload);
        setCreatedMeeting(created?.data || null);
        setCreatedOpen(true);
        if (created?.data?.id && staff?.email) {
          await post(`/api/zoom/meetings/${created.data.id}/invitation-email`, { to: [staff.email] });
          setInviteStatus('Invitation email sent to you.');
        }
      }
      if (!editing?.id) {
        setDialogOpen(false);
        setEditing(null);
      }
      await load();
    } catch (e) {
      setDialogError(e?.message || 'Save failed');
    } finally {
      setDialogSaving(false);
    }
  };

  const confirmCancel = async () => {
    if (!canceling?.id) return;
    setError('');
    setOk('');
    try {
      await del(`/api/zoom/meetings/${canceling.id}`);
      setOk('Meeting canceled');
      setCancelOpen(false);
      setCanceling(null);
      await load();
    } catch (e) {
      setError(e?.message || 'Cancel failed');
    }
  };

  const startPreview = buildDateInZone(form.date, form.time, timeZone);
  const endPreview = startPreview ? new Date(startPreview.getTime() + (Number(form.duration) * 60 * 1000)) : null;

  const formatDateTimeInActiveZone = (iso) => formatDateTimeInZoneWithZone(iso, timeZone);
  const endPreviewLabel = endPreview ? formatDateTimeAmPm(endPreview, timeZone) : '';
  const filteredRows = rows.filter((m) => {
    if (!search.trim()) return true;
    return String(m.title || '').toLowerCase().includes(search.trim().toLowerCase());
  });

  const goNext = async () => {
    if (!nextPageToken) return;
    setPageStack((prev) => [...prev, nextPageToken]);
    await load(nextPageToken);
  };

  const goPrev = async () => {
    setPageStack((prev) => {
      if (prev.length <= 1) {
        load().catch(() => {});
        return [];
      }
      const next = prev.slice(0, -1);
      const token = next[next.length - 1];
      load(token).catch(() => {});
      return next;
    });
  };

  const copyJoinLink = async (url) => {
    try {
      if (!url) return;
      await navigator.clipboard.writeText(url);
      setOk('Join link copied');
    } catch {
      setError('Failed to copy link');
    }
  };

  const sendInvitation = async (meetingId, emails) => {
    if (!meetingId) return;
    const targets = String(emails || '').split(',').map(s => s.trim()).filter(Boolean);
    if (targets.length === 0) {
      setInviteStatus('Please enter at least one email.');
      return;
    }
    setInviteSending(true);
    setInviteStatus('');
    try {
      await post(`/api/zoom/meetings/${meetingId}/invitation-email`, { to: targets });
      setInviteStatus('Invitation sent successfully.');
    } catch (e) {
      setInviteStatus(e?.message || 'Failed to send invitation.');
    } finally {
      setInviteSending(false);
    }
  };

  const sendInvitationToMe = async (meetingId) => {
    const myEmail = staff?.email;
    if (!myEmail) {
      setInviteStatus('Your email is not available.');
      return;
    }
    await sendInvitation(meetingId, myEmail);
  };

  const isPastMeeting = (meeting) => {
    try {
      const now = Date.now();
      if (meeting?.end_time) {
        return new Date(meeting.end_time).getTime() < now;
      }
      if (meeting?.start_time) {
        return new Date(meeting.start_time).getTime() < now;
      }
      return false;
    } catch {
      return false;
    }
  };

  const canManageMeeting = (meeting) => {
    if (hasPermission('admin:full')) return true;
    const myEmail = String(staff?.email || '').toLowerCase();
    const createdEmail = String(meeting?.created_by_email || '').toLowerCase();
    return !!myEmail && !!createdEmail && myEmail === createdEmail;
  };

  const getManageState = (meeting) => {
    if (isPastMeeting(meeting)) {
      return { allowed: false, reason: 'Past meetings cannot be edited or canceled' };
    }
    const canEdit = hasPermission('meetings:edit');
    const canCancel = hasPermission('meetings:cancel');
    if (canManageMeeting(meeting) && canEdit && canCancel) {
      return { allowed: true, reason: '' };
    }
    return { allowed: false, reason: 'Only the creator or an admin can edit or cancel' };
  };

  const now = new Date();
  const nowIso = now.toISOString();
  const minDate = formatDateInZoneWithZone(nowIso, timeZone) || nowIso.slice(0, 10);
  const minTimeToday = formatTimeInZoneWithZone(nowIso, timeZone) || nowIso.slice(11, 16);

  return (
    <PageLayout showAdminActions contentMaxWidth="lg" showHeaderImage={false} showOrgHeader portalTitle="ICCT Portal">
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Meetings</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={openCreate}>Create Meeting</Button>
          <Button variant="outlined" onClick={() => load().catch(() => {})} disabled={loading}>Refresh</Button>
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(e, value) => value && setFilter(value)}
          size="small"
        >
          <ToggleButton value="upcoming">Upcoming</ToggleButton>
          <ToggleButton value="past">Past</ToggleButton>
          <ToggleButton value="recurring">Recurring</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
        <TextField
          size="small"
          label="Search title"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <FormControl size="small">
          <InputLabel>Page size</InputLabel>
          <Select
            label="Page size"
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {[25, 50, 100].map((size) => (
              <MenuItem key={size} value={size}>{size}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          Times shown in {timeZone}
        </Typography>
      </Stack>

      {error && <Box sx={{ mb: 2, color: 'error.main' }}>{error}</Box>}
      {ok && <Box sx={{ mb: 2, color: 'success.main' }}>{ok}</Box>}

      <Box sx={{ bgcolor: 'background.paper', borderRadius: 2, border: '1px solid #eee' }}>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6, gap: 2 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Please wait, loading meetings...</Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>End</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Created By</TableCell>
                <TableCell>Join Link</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>No meetings</TableCell>
                </TableRow>
              )}
            {filteredRows.map((m) => {
              const manageState = getManageState(m);
              return (
                <TableRow key={m.id}>
                  <TableCell>{m.title}</TableCell>
                  <TableCell>{formatDateTimeInActiveZone(m.start_time)}</TableCell>
                  <TableCell>{formatDateTimeInActiveZone(m.end_time)}</TableCell>
                  <TableCell>{m.duration_minutes ? `${m.duration_minutes} min` : '--'}</TableCell>
                  <TableCell>{m.created_by_name || m.created_by_email ? (m.created_by_name || m.created_by_email) : 'Admin'}</TableCell>
                  <TableCell>
                    {m.join_url ? (
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Copy link">
                          <IconButton
                            size="medium"
                            onClick={() => copyJoinLink(m.join_url)}
                            aria-label="Copy join link"
                            sx={{ bgcolor: 'action.hover' }}
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Email invitation">
                          <IconButton
                            size="medium"
                            onClick={() => openInviteDialog(m)}
                            aria-label="Email invitation"
                            sx={{ bgcolor: 'action.hover' }}
                          >
                            <EmailIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    ) : (
                      '--'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" variant="outlined" onClick={() => openView(m)}>View</Button>
                      {manageState.allowed && (
                        <>
                          <Button size="small" variant="outlined" onClick={() => openEdit(m)}>Edit</Button>
                          <Button size="small" color="error" variant="contained" onClick={() => openCancel(m)}>Cancel</Button>
                        </>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
            </TableBody>
          </Table>
        )}
      </Box>

      <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center" justifyContent="flex-end">
        <Button variant="outlined" onClick={goPrev} disabled={pageStack.length === 0 || loading}>
          Previous
        </Button>
        <Button variant="contained" onClick={goNext} disabled={!nextPageToken || loading}>
          Next
        </Button>
      </Stack>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{editing ? 'Edit Meeting' : 'Create Meeting'}</Typography>
          <IconButton onClick={closeDialog} size="small" aria-label="Close dialog">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {dialogError && (
              <Box sx={{ color: 'error.main', fontWeight: 600 }}>
                {dialogError}
              </Box>
            )}
            {editSuccess && (
              <Box sx={{ color: 'success.main', fontWeight: 600 }}>
                {editSuccess}
              </Box>
            )}
            {inviteStatus && (
              <Box sx={{ color: inviteStatus.includes('Failed') || inviteStatus.includes('Please') ? 'error.main' : 'success.main', fontWeight: 600 }}>
                {inviteStatus}
              </Box>
            )}
            <TextField
              label="Title *"
              value={form.title}
              onChange={(e) => {
                setForm({ ...form, title: e.target.value });
                if (dialogFieldErrors.title) setDialogFieldErrors((prev) => ({ ...prev, title: '' }));
              }}
              fullWidth
              required
              error={!!dialogFieldErrors.title}
              helperText={dialogFieldErrors.title || ''}
            />
            <TextField
              label="Agenda"
              value={form.agenda}
              onChange={(e) => setForm({ ...form, agenda: e.target.value })}
              fullWidth
              multiline
              minRows={3}
              placeholder="Add agenda or meeting details"
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="Start Date *"
                type="date"
                value={form.date}
                onChange={(e) => {
                  setForm({ ...form, date: e.target.value });
                  if (dialogFieldErrors.date) setDialogFieldErrors((prev) => ({ ...prev, date: '' }));
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText={dialogFieldErrors.date || `Timezone: ${timeZone}`}
                inputProps={{ min: minDate }}
                required
                error={!!dialogFieldErrors.date}
              />
              <TextField
                label="Start Time *"
                type="time"
                value={form.time}
                onChange={(e) => {
                  setForm({ ...form, time: e.target.value });
                  if (dialogFieldErrors.time) setDialogFieldErrors((prev) => ({ ...prev, time: '' }));
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText={dialogFieldErrors.time || `Timezone: ${timeZone}`}
                inputProps={{ min: form.date === minDate ? minTimeToday : undefined }}
                required
                error={!!dialogFieldErrors.time}
              />
            </Stack>
            <FormControl fullWidth error={!!dialogFieldErrors.duration}>
              <InputLabel required>Duration *</InputLabel>
              <Select
                label="Duration"
                value={form.duration}
                onChange={(e) => {
                  setForm({ ...form, duration: e.target.value });
                  if (dialogFieldErrors.duration) setDialogFieldErrors((prev) => ({ ...prev, duration: '' }));
                }}
                required
              >
                {DURATION_OPTIONS.map((mins) => (
                  <MenuItem key={mins} value={mins}>{mins} minutes</MenuItem>
                ))}
              </Select>
              {dialogFieldErrors.duration ? (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                  {dialogFieldErrors.duration}
                </Typography>
              ) : null}
            </FormControl>
            <TextField
              label="End (read-only)"
              value={endPreviewLabel || '--'}
              fullWidth
              InputProps={{ readOnly: true }}
              helperText="Calculated automatically based on start time and duration"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Close</Button>
          <Button variant="contained" onClick={saveMeeting} disabled={dialogSaving}>
            {dialogSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Meeting Details</Typography>
          <IconButton onClick={() => setViewOpen(false)} size="small" aria-label="Close dialog">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" value={viewing?.title || ''} fullWidth InputProps={{ readOnly: true }} />
            <TextField label="Agenda" value={viewing?.agenda || ''} fullWidth InputProps={{ readOnly: true }} multiline minRows={3} />
            <TextField label="Start" value={formatDateTimeInActiveZone(viewing?.start_time)} fullWidth InputProps={{ readOnly: true }} />
            <TextField label="End" value={formatDateTimeInActiveZone(viewing?.end_time)} fullWidth InputProps={{ readOnly: true }} />
            <TextField label="Duration" value={viewing?.duration_minutes ? `${viewing.duration_minutes} minutes` : ''} fullWidth InputProps={{ readOnly: true }} />
            <TextField label="Join Link" value={viewing?.join_url || ''} fullWidth InputProps={{ readOnly: true }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
          {viewing && canManageMeeting(viewing) && hasPermission('meetings:edit') && (
            <Button
              variant="contained"
              onClick={() => {
                setViewOpen(false);
                openEdit(viewing);
              }}
            >
              Edit
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={cancelOpen} onClose={() => setCancelOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Cancel Meeting?</Typography>
          <IconButton onClick={() => setCancelOpen(false)} size="small" aria-label="Close dialog">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Are you sure you want to cancel "{canceling?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelOpen(false)}>No</Button>
          <Button color="error" variant="contained" onClick={confirmCancel}>Yes, Cancel</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createdOpen} onClose={() => setCreatedOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Meeting Created</Typography>
          <IconButton onClick={() => setCreatedOpen(false)} size="small" aria-label="Close dialog">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" value={createdMeeting?.title || ''} fullWidth InputProps={{ readOnly: true }} />
            <TextField label="Start" value={formatDateTimeInActiveZone(createdMeeting?.start_time)} fullWidth InputProps={{ readOnly: true }} />
            <TextField label="End" value={formatDateTimeInActiveZone(createdMeeting?.end_time)} fullWidth InputProps={{ readOnly: true }} />
            <TextField label="Duration" value={createdMeeting?.duration_minutes ? `${createdMeeting.duration_minutes} minutes` : ''} fullWidth InputProps={{ readOnly: true }} />
            <TextField label="Agenda" value={createdMeeting?.agenda || ''} fullWidth InputProps={{ readOnly: true }} multiline minRows={3} />
            <TextField
              label="Join Link"
              value={createdMeeting?.join_url || ''}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: createdMeeting?.join_url ? (
                  <InputAdornment position="end">
                    <Tooltip title="Copy link">
                      <IconButton onClick={() => copyJoinLink(createdMeeting.join_url)} size="small" aria-label="Copy join link">
                        <ContentCopyIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null
              }}
            />
            <TextField
              label="Send invitation to others (comma separated emails)"
              value={inviteEmails}
              onChange={(e) => setInviteEmails(e.target.value)}
              fullWidth
            />
            {inviteStatus && (
              <Box sx={{ color: inviteStatus.includes('Failed') || inviteStatus.includes('Please') ? 'error.main' : 'success.main', fontWeight: 600 }}>
                {inviteStatus}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => sendInvitation(createdMeeting?.id, inviteEmails)}
            disabled={inviteSending}
          >
            {inviteSending ? 'Sending...' : 'Send Invitation'}
          </Button>
          <Button onClick={() => setCreatedOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Email Invitation</Typography>
          <IconButton onClick={() => setInviteDialogOpen(false)} size="small" aria-label="Close dialog">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" value={inviteMeeting?.title || ''} fullWidth InputProps={{ readOnly: true }} />
            <TextField label="Start" value={formatDateTimeInActiveZone(inviteMeeting?.start_time)} fullWidth InputProps={{ readOnly: true }} />
            <TextField label="End" value={formatDateTimeInActiveZone(inviteMeeting?.end_time)} fullWidth InputProps={{ readOnly: true }} />
            <TextField
              label="Send invitation to others (comma separated emails)"
              value={inviteEmails}
              onChange={(e) => setInviteEmails(e.target.value)}
              fullWidth
            />
            {inviteStatus && (
              <Box sx={{ color: inviteStatus.includes('Failed') || inviteStatus.includes('Please') ? 'error.main' : 'success.main', fontWeight: 600 }}>
                {inviteStatus}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => sendInvitationToMe(inviteMeeting?.id)}
            disabled={inviteSending}
          >
            Email to Me
          </Button>
          <Button
            variant="contained"
            onClick={() => sendInvitation(inviteMeeting?.id, inviteEmails)}
            disabled={inviteSending}
          >
            {inviteSending ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}
